import React from "react";

const PHQForm = ({ formData, handleChange }) => {
    const questions = [
        "Little interest or pleasure in doing things",
        "Feeling down, depressed, or hopeless",
        "Trouble falling or staying asleep, or sleeping too much",
        "Feeling tired or having little energy",
        "Poor appetite or overeating",
        "Feeling bad about yourself — or that you are a failure or have let yourself or your family down",
        "Trouble concentrating on things, such as reading the newspaper or watching television",
        "Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual",
        "Thoughts that you would be better off dead or of hurting yourself in some way"
    ];

    const options = [
        { value: 0, label: "Not at all" },
        { value: 1, label: "Several days" },
        { value: 2, label: "More than half the days" },
        { value: 3, label: "Nearly every day" }
    ];

    const difficultyOptions = [
        { value: "not_difficult", label: "Not difficult at all" },
        { value: "somewhat", label: "Somewhat difficult" },
        { value: "very", label: "Very difficult" },
        { value: "extremely", label: "Extremely difficult" }
    ];

    // ★★★★★ EXACT SAME LOGIC AS AnxietyForm (Dynamic & Correct) ★★★★★
    const calculateScores = () => {
        let counts = [0, 0, 0, 0]; // how many q’s have 0,1,2,3
        let total = 0;

        for (let i = 1; i <= 9; i++) {
            const val = parseInt(formData[`q${i}`] || "0", 10);

            if (val >= 0 && val <= 3) {
                counts[val]++;
                total += val;
            }
        }

        // Subtotals follow same formula as AnxietyForm
        const subtotals = [
            0,               // 0 × count
            1 * counts[1],   // 1 × count
            2 * counts[2],   // 2 × count
            3 * counts[3]    // 3 × count
        ];

        return { subtotals, total };
    };

    const { subtotals, total } = calculateScores();

    const getSeverity = (score) => {
        if (score <= 4) return { text: "None-minimal", color: "success" };
        if (score <= 9) return { text: "Mild", color: "info" };
        if (score <= 14) return { text: "Moderate", color: "warning" };
        if (score <= 19) return { text: "Moderately severe", color: "orange" };
        return { text: "Severe", color: "danger" };
    };

    const severity = getSeverity(total);

    return (
        <>
            {/* Sticky Guidelines Button */}
            <div className="row mb-4">
                <div className="col-md-3">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm w-100"
                        data-bs-toggle="modal"
                        data-bs-target="#phqGuidelines"
                    >
                        <i className="ri-information-line me-2"></i>
                        PHQ-9 Guidelines & Scoring
                    </button>
                </div>
            </div>

            <div className="p-3" style={{ maxHeight: "80vh", overflowY: "auto", scrollbarWidth: "thin" }}>
                {/* Top Fields */}
                <div className="row g-3 mb-5">
                    <div className="col-md-6">
                        <div className="form-floating form-floating-outline">
                            <input
                                type="text"
                                className="form-control"
                                name="clientName"
                                value={formData.clientName || ""}
                                onChange={handleChange}
                                readOnly={!!formData.patientId} // ⭐ ADD THIS PROPERTY ⭐
                            />
                            <label>Client Name</label>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-floating form-floating-outline">
                            <input
                                type="date"
                                className="form-control"
                                name="date"
                                value={formData.date || ""}
                                onChange={handleChange}
                            />
                            <label>Date</label>
                        </div>
                    </div>
                </div>

                <div className="accordion" id="phqAccordion">
                    {/* PHQ-9 Questions */}
                    {questions.map((q, index) => (
                        <div className="accordion-item mb-3 shadow-sm rounded" key={index + 1}>
                            <h2 className="accordion-header">
                                <button
                                    button className="accordion-button collapsed fw-medium" type="button" data-bs-toggle="collapse"
                                    data-bs-target={`#q${index + 1}`}
                                >
                                    <span className="me-3 fw-bold">{index + 1}.</span>
                                    {q}
                                </button>
                            </h2>

                            <div id={`q${index + 1}`} className="accordion-collapse collapse">
                                <div className="accordion-body pt-4">
                                    <div className="row g-5">
                                        {options.map((opt) => (
                                            <div className="col-lg-6" key={opt.value}>
                                                <div className="form-check mb-3">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name={`q${index + 1}`}
                                                        id={`q${index + 1}_opt${opt.value}`}
                                                        value={String(opt.value)}
                                                        checked={formData[`q${index + 1}`] === String(opt.value)}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label fw-medium d-flex align-items-start" htmlFor={`q${index + 1}_opt${opt.value}`}>
                                                        <span className="text-primary fw-bold me-3">{opt.value}</span>
                                                        <span>{opt.label}</span>
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Score Calculation */}
                    <div className="mt-5 pt-4 border-top">
                        <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">
                            Score Calculation
                        </small>

                        <div className="row g-3">
                            <div className="col-md-2">
                                <div className="form-floating form-floating-outline mb-3">
                                    <input type="text" className="form-control" value={subtotals[0]} readOnly />
                                    <label>Not at all (0):</label>
                                </div>
                            </div>

                            <div className="col-md-2">
                                <div className="form-floating form-floating-outline mb-3">
                                    <input type="text" className="form-control" value={subtotals[1]} readOnly />
                                    <label>Several days (1):</label>
                                </div>
                            </div>

                            <div className="col-md-2">
                                <div className="form-floating form-floating-outline mb-3">
                                    <input type="text" className="form-control" value={subtotals[2]} readOnly />
                                    <label>More than half (2):</label>
                                </div>
                            </div>

                            <div className="col-md-2">
                                <div className="form-floating form-floating-outline mb-3">
                                    <input type="text" className="form-control" value={subtotals[3]} readOnly />
                                    <label>Nearly every (3):</label>
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className="form-floating form-floating-outline mb-3">
                                    <input type="text" className="form-control" value={total} readOnly />
                                    <label>Total Score</label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Difficulty Question */}
                    <div className="accordion-item mb-4 shadow-sm rounded">
                        <h2 className="accordion-header">
                            <button className="accordion-button collapsed fw-semibold" type="button" data-bs-toggle="collapse" data-bs-target="#difficultySection">
                                If you checked off any problems, how difficult have these problems made it for you?
                            </button>
                        </h2>
                        <div id="difficultySection" className="accordion-collapse collapse show">
                            <div className="accordion-body">
                                <div className="row g-4 justify-content-center">
                                    {difficultyOptions.map((opt) => (
                                        <div className="col-lg-3 col-md-6" key={opt.value}>
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="difficulty"
                                                    id={`difficulty_opt${opt.value}`}
                                                    value={opt.value}
                                                    checked={formData.difficulty === opt.value}
                                                    onChange={handleChange}
                                                />
                                                <label className="form-check-label fw-medium" htmlFor={`difficulty_opt${opt.value}`}>{opt.label}</label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Guidelines Modal */}
                <div className="modal fade" id="phqGuidelines" tabIndex="-1">
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-body">
                                {/* exact same content */}
                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">
                                    Major depressive disorder (MDD) is suggested if:
                                </small>
                                <ul>
                                    <li>5 or more items are ≥ “more than half the days”</li>
                                    <li>Either item 1 or 2 is ≥ “more than half the days”</li>
                                </ul>

                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">
                                    Other depressive syndrome is suggested if:
                                </small>
                                <ul>
                                    <li>2–4 items are ≥ “more than half the days”</li>
                                    <li>Either item 1 or 2 is ≥ “more than half the days”</li>
                                </ul>

                                <table className="table table-bordered mt-4">
                                    <thead className="table-primary">
                                        <tr>
                                            <th>Score</th>
                                            <th>Severity</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr><td>0–4</td><td>None-minimal</td><td>No treatment usually needed</td></tr>
                                        <tr><td>5–9</td><td>Mild</td><td>Use clinical judgment</td></tr>
                                        <tr><td>10–14</td><td>Moderate</td><td>Use clinical judgment</td></tr>
                                        <tr><td>15–19</td><td>Moderately severe</td><td>Consider therapy or medication</td></tr>
                                        <tr><td>20–27</td><td>Severe</td><td>Medication ± therapy</td></tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PHQForm;
